import { JawsScanSummaryResponse } from '@/schema/jawsScan';

export interface ScanWorkflow {
  key: 'segmentation' | 'correction' | 'export' | 'treatment-plan';
  label: string;
  path?: string;
  disabled: boolean;
}

/**
 * Which stages a scan can be opened at, and where each one lives. Shared so the clinic
 * toolbar and the admin console's case panel cannot drift apart on the rules — the two
 * are looking at the same case, and a stage that is closed in one must be closed in both.
 *
 * 'Printing' sits between Corrected and Braces Created, so anything a corrected case could
 * still open stays open once production has taken the models.
 */
export function scanWorkflows(
  patientId: string | undefined,
  scan: JawsScanSummaryResponse | null | undefined,
): ScanWorkflow[] {
  const status = scan?.status ?? '';
  const canOpen = (stages: string[]) => Boolean(scan) && stages.includes(status);

  return [
    {
      key: 'segmentation',
      label: 'Segment',
      path: patientId && scan ? `/patients/${patientId}/scans/${scan.id}/segmentation` : undefined,
      disabled: !canOpen(['Empty', 'Scanned', 'Segmented', 'Corrected', 'Printing', 'Braces Created', 'Printed']),
    },
    {
      key: 'correction',
      label: 'Correct',
      path:
        patientId && scan?.jawsSegmentationId
          ? `/patients/${patientId}/scans/${scan.id}/correction`
          : undefined,
      disabled: !canOpen(['Segmented', 'Corrected', 'Printing', 'Braces Created', 'Printed']),
    },
    {
      key: 'export',
      label: 'Export',
      path: patientId && scan ? `/patients/${patientId}/scans/${scan.id}/export` : undefined,
      disabled: !canOpen(['Corrected', 'Printing', 'Braces Created', 'Printed']),
    },
    {
      key: 'treatment-plan',
      label: 'Treatment plan',
      path: scan ? `/treatment-plan/${scan.id}` : undefined,
      disabled: !scan || (scan.jawsCorrectionIds?.length ?? 0) === 0,
    },
  ];
}
